// material.ts — 재료비(1ea) 순수 계산: 원자재 봉재 중량·절단 손실·표면처리/열처리.
//
// 백엔드 `apps/api/api/core/cost/material.py` 와 동일한 수식/결과를 유지해야 한다
// (CLAUDE.md 패리티 — 한쪽 수정 시 양쪽 + 테스트 동시 수정).
//
// 비중/NET 은 상위 이슈(#1/#10)의 단일 출처를 재사용한다 — 여기서 다시 정의하지 않는다.
//   import { densityOf } from "@/lib/cost/master";
//   NET 중량은 입력으로 받는다 (netWeight from "@/lib/cost/netweight").
//
// 재료비계(matCostTotal, P10)는 #5(제조원가)·#6(재료관리비)의 입력으로 연계된다:
//   import { materialCost, type MaterialInput, type MaterialResult } from "@/lib/cost/material";
//
// Excel 출처 CNC1/BOLT1/NUT1: 봉재/생산량 W3:W9·Y3:AE6, 재료비계 N4:Q10(P10=SUM(P5:Q9)).
//
// 공법(method)별 분기:
//   - CNC  : 원재료 = GROSS 기준,  표면처리(산처리) = NET×0.97,  열처리 없음
//   - BOLT : 원재료 = NET×1.02(scrap), 표면처리 = NET,   열처리 = NET
//   - NUT  : 원재료 = NET×1.02(scrap), 표면처리 = NET,   열처리 없음

import { densityOf } from "@/lib/cost/master";

// ----- 상수 (Excel 시트 값 — magic number 금지) -----

// 봉재 유효장(mm). Excel W7 = 2250 / 절단길이.
export const BAR_EFFECTIVE_LEN = 2250;

// 원주율. Excel 봉재 중량 W4 의 AB4 열은 π 가 아니라 3.14 를 쓴다(시트 그대로).
// NET(#10) 의 Math.PI 와는 별개의 봉재 체적용 상수 — 타겟 P10 재현을 위해 3.14 고정.
export const BAR_PI = 3.14;

// 절단폭 구간 테이블 (Excel Z6 의 중첩 IF): 외경 ≤4→1.5 / ≤10→2.0 / ≤20→2.2 / ≤30→2.5
export const CUT_WIDTH_STEPS: ReadonlyArray<readonly [number, number]> = [
  [4, 1.5],
  [10, 2.0],
  [20, 2.2],
  [30, 2.5],
];

// 공법별 중량 계수.
export const SCRAP_FACTOR = 1.02; // BOLT/NUT 원재료 NET 할증
export const ACID_TREAT_FACTOR = 0.97; // CNC 표면처리(산처리) NET 감면

export type Method = "CNC" | "BOLT" | "NUT";

// 외경(mm)에 대응하는 절단폭(mm)을 구간 테이블에서 조회 (Excel Z6).
export function cutWidthOf(outerD: number): number {
  for (const [upper, width] of CUT_WIDTH_STEPS) {
    if (outerD <= upper) return width;
  }
  throw new Error(
    `outerD=${outerD} 가 절단폭 구간 테이블 최대(${CUT_WIDTH_STEPS[CUT_WIDTH_STEPS.length - 1][0]})를 초과`
  );
}

// ----- 입력 / 출력 -----

export interface MaterialInput {
  series: string; // 소재계열 (master 비중 lookup 키)
  outerD: number; // 외경(mm)
  length: number; // 제품기장(mm) (Excel Y6)
  rawUnitPrice: number; // 원자재단가(₩/Kg). 봉재 단가(W3) 겸 원재료 ₩/g(=단가/1000).
  surfaceUnitPrice: number; // 표면처리 단가(₩/g)
  heatUnitPrice: number; // 열처리 단가(₩/g)
  netWeight: number; // 정미중량 NET(g) — #10 산출값
  method: Method;
  barEffectiveLen?: number; // 봉재 유효장(mm), 기본 2250
  rawTotalLen?: number; // 원자재 전장(mm) (Excel AE4), 기본 2500
}

export interface MaterialResult {
  barWeightKg: number; // 1본 Kg (W4)
  cutLength: number; // 절단길이 (W6)
  unitsPerBar: number; // 1본 생산량 (W7)
  matCost: number; // 재료비 ₩/ea (W8)
  grossWeight: number; // 제품중량 GROSS (W9)
  matCostTotal: number; // 재료비계 (P10) = 원재료 + 표면처리 + 열처리
}

// 1ea 재료비를 산출 (Excel CNC1/BOLT1/NUT1 봉재→재료비계 체인).
export function materialCost(input: MaterialInput): MaterialResult {
  const barEffectiveLen = input.barEffectiveLen ?? BAR_EFFECTIVE_LEN;
  const rawTotalLen = input.rawTotalLen ?? 2500;

  const radius = input.outerD / 2;
  const gPerMm3 = densityOf(input.series) / 1000; // Excel AD 열 = 비중/1000

  // 봉재 중량/생산량 (W4~W9)
  const barWeight = radius * radius * BAR_PI * gPerMm3 * rawTotalLen; // W4
  const barMaterialCost = barWeight * input.rawUnitPrice; // W5
  const cutLength = input.length + cutWidthOf(input.outerD); // W6
  const unitsPerBar = barEffectiveLen / cutLength; // W7
  const matCost = barMaterialCost / unitsPerBar; // W8 (₩/ea)
  const grossWeight = barWeight / unitsPerBar; // W9 (GROSS)

  // 원재료 ₩/g = 원자재단가 / 1000 (Excel I5 = W3/1000)
  const rawPricePerG = input.rawUnitPrice / 1000;
  const net = input.netWeight;

  // 공법별 재료비계 (P10 = 원재료 + 표면처리 + 열처리)
  let raw: number;
  let surface: number;
  let heat: number;
  if (input.method === "CNC") {
    raw = rawPricePerG * grossWeight; // GROSS 기준 (= W8/1000)
    surface = input.surfaceUnitPrice * net * ACID_TREAT_FACTOR; // 산처리 NET×0.97
    heat = 0;
  } else if (input.method === "BOLT") {
    raw = rawPricePerG * net * SCRAP_FACTOR;
    surface = input.surfaceUnitPrice * net;
    heat = input.heatUnitPrice * net;
  } else {
    // NUT
    raw = rawPricePerG * net * SCRAP_FACTOR;
    surface = input.surfaceUnitPrice * net;
    heat = 0;
  }

  const matCostTotal = raw + surface + heat;

  return {
    barWeightKg: barWeight,
    cutLength,
    unitsPerBar,
    matCost,
    grossWeight,
    matCostTotal,
  };
}
